import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeHero from "../components/HomeHero";
import WhyBlendSafe from "../components/WhyBlendSafe";
import RecentChecksSection from "../components/RecentChecksSection";
import CommonQuestions from "../components/CommonQuestions";
import RoutineExplorer from "../components/RoutineExplorer";
import HowItWorksFlow from "../components/HowItWorksFlow";
import CategoryShowcase from "../components/CategoryShowcase";
import EvidenceSection from "../components/EvidenceSection";
import WhatUnlocksAfterLaunchSection from "../components/WhatUnlocksAfterLaunchSection";
import WhatYouGetSection from "../components/WhatYouGetSection";
import FoundingSpotClaim from "../components/FoundingSpotClaim";
import FaqSection from "../components/FaqSection";
import AnnouncementBar from "../components/AnnouncementBar";
import SectionHeading from "../components/SectionHeading";
import { fetchHomepageContent } from "../lib/api";
import { recordVisit } from "../lib/streak";
import { initTemplateAnimations } from "../lib/templateAnimations";
import "../styles/tactile.css";

export default function Landing() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);

  useEffect(() => { recordVisit(); }, []);

  useEffect(() => {
    document.body.classList.add("home-tactile");
    return () => document.body.classList.remove("home-tactile");
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchHomepageContent()
      .then((d) => { if (!cancelled) setContent(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = initTemplateAnimations(document);
    return cleanup;
  }, [content]);

  return (
    <main data-testid="landing-page" className="bg-cream-50 overflow-x-clip">
      <AnnouncementBar enabled />

      {/* 1. HERO — template dots hero with checker + live stat marquee */}
      <HomeHero stats={content?.stats} />

      {/* 2. WHY BLENDSAFE */}
      <WhyBlendSafe methodologyHref="/methodology" />

      {/* 3. PICK UP WHERE YOU LEFT OFF (only if user has past checks) */}
      <div data-animate="fade-up-1"><RecentChecksSection /></div>

      {/* 5. CASE STUDIES — common questions (live verdicts) */}
      <CommonQuestions questions={content?.questions} />

      {/* 6. SERVICES — routine explorer + interaction library */}
      <div data-animate="fade-up-1"><RoutineExplorer /></div>

      {/* 7. PROCESS — how it works (self-contained autoplay walkthrough, owns id="how") */}
      <div data-animate="fade-up-1"><HowItWorksFlow /></div>

      {/* 8. INDUSTRIES — browse by medication category */}
      <section id="categories" className="bg-cream-50 py-16 md:py-24">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10">
          <div className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-4" data-animate="fade-up-1">
            <SectionHeading eyebrow="By condition or medication" title="Start with your category" />
            <button
              onClick={() => navigate("/check")}
              data-testid="landing-browse-all"
              className="inline-flex items-center min-h-[44px] sm:min-h-0 text-[13px] font-semibold text-ink underline underline-offset-4 decoration-peach-400 hover:decoration-peach-500"
            >
              OPEN THE CHECKER →
            </button>
          </div>
          <div data-animate="fade-up-2"><CategoryShowcase /></div>
        </div>
      </section>

      {/* 9. REVIEWS / EVIDENCE — science + live checks strip */}
      <section id="trust" className="bg-white py-16 md:py-24 border-y border-surface-line">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10" data-animate="fade-up-1">
          <EvidenceSection />
        </div>
      </section>

      {/* 10. ROADMAP — what unlocks after launch */}
      <div data-animate="fade-up-1"><WhatUnlocksAfterLaunchSection /></div>

      {/* 13. PRICING — free vs founding */}
      <div data-animate="fade-up-1"><WhatYouGetSection /></div>

      {/* 14. BANNER CTA — founding member waitlist */}
      <FoundingSpotClaim founding={content?.founding} />

      {/* 15. FAQ (two-column) */}
      <FaqSection />

    </main>
  );
}
